import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  IMMOBILIARE_REQUIRED_FIELDS,
  IDEALISTA_REQUIRED_FIELDS,
  checkImmobiliareCompleteness,
  checkIdealistaCompleteness,
} from "@/lib/portalRequirements";

// iter v28 r320/r321 — Set dei key obbligatori per accesso O(1).
// Unione dei campi richiesti da Immobiliare e Idealista per marcare
// l'asterisco "*" sui campi del form.
const REQUIRED_SET = new Set([
  ...IMMOBILIARE_REQUIRED_FIELDS.map(f => f.key),
  ...IDEALISTA_REQUIRED_FIELDS.map(f => f.key),
]);

/**
 * PropertyEditModal — modifica i dati editoriali dell'immobile.
 *
 * Aperto da PropertyDetail tramite il pulsante "Modifica dati" nell'header.
 * I dati richiesti dal PUT /api/properties/{pid} sono in `PropertyCreate`
 * (vedi backend/models.py). Qui esponiamo i campi che l'agente vuole poter
 * cambiare anche DOPO la creazione: titolo, tipo, prezzo, mq, locali, piano,
 * città, classe energetica.
 *
 * iter 261: nasce dal feedback utente "non riesco più a modificare il
 * titolo e i mq dopo la creazione".
 */
export default function PropertyEditModal({ open, property, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && property) {
      setForm({
        title: property.title || "",
        property_type: property.property_type || "Appartamento",
        listing_type: property.listing_type || "vendita",
        price: property.price ?? "",
        surface: property.surface ?? "",
        rooms: property.rooms ?? "",
        bathrooms: property.bathrooms ?? "",
        floor: property.floor || "",
        city: property.city || "",
        address: property.address || "",
        // iter 310 r17 — toggle privacy
        hide_price: !!property.hide_price,
        hide_address: !!property.hide_address,
        energy_class: property.energy_class || "",
        year_built: property.year_built ?? "",
        year_renovated: property.year_renovated ?? "",
        heating: property.heating || "",
        exposure: property.exposure || "",
        // iter v28 r277 — Campi extra per estensione Chrome (Immobiliare ecc.)
        bedrooms: property.bedrooms ?? "",
        kitchens: property.kitchens ?? "",
        total_floors: property.total_floors ?? "",
        ipe: property.ipe ?? "",
        air_conditioning: property.air_conditioning || "",
        furnished: property.furnished || "",
        fixtures: property.fixtures || "",
        condition: property.condition || "",
        property_ownership: property.property_ownership || "",
        elevators: property.elevators ?? "",
        garage_spots: property.garage_spots ?? "",
        outdoor_parking: property.outdoor_parking ?? "",
        monthly_fees: property.monthly_fees ?? "",
        // iter v28 r278 — Campi extra allineamento Immobiliare
        heating_fuel: property.heating_fuel || "",
        cadastral_category: property.cadastral_category || "",
        availability_status: property.availability_status || "",
        available_from: property.available_from || "",
        cellar: !!property.cellar,
        cellar_sqm: property.cellar_sqm ?? "",
        attic: !!property.attic,
        attic_sqm: property.attic_sqm ?? "",
        taverna: !!property.taverna,
        pool: property.pool || "",
        accessible: !!property.accessible,
        concierge: property.concierge || "",
        contract_registration: property.contract_registration || "",
        // iter v28 r345 — Caratteristiche aggiuntive per Idealista
        terrace: !!property.terrace,
        balcony: !!property.balcony,
        garden: !!property.garden,
        wardrobe: !!property.wardrobe,
      });
    }
  }, [open, property]);

  if (!open) return null;

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const updateNum = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v === "" ? "" : Number(v) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Backend PUT richiede TUTTI i campi del PropertyCreate (replace
      // semantica). Quindi mergiamo i campi modificati con quelli esistenti
      // dell'immobile per non perdere ai_description, features, ecc.
      const payload = {
        ...property,
        ...form,
        // Numerici: stringa vuota → null
        price: form.price === "" ? null : Number(form.price),
        surface: form.surface === "" ? null : Number(form.surface),
        rooms: form.rooms === "" ? null : Number(form.rooms),
        bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
        year_built: form.year_built === "" ? null : Number(form.year_built),
        year_renovated: form.year_renovated === "" ? null : Number(form.year_renovated),
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        kitchens: form.kitchens === "" ? null : Number(form.kitchens),
        total_floors: form.total_floors === "" ? null : Number(form.total_floors),
        ipe: form.ipe === "" ? null : Number(form.ipe),
        elevators: form.elevators === "" ? null : Number(form.elevators),
        garage_spots: form.garage_spots === "" ? null : Number(form.garage_spots),
        outdoor_parking: form.outdoor_parking === "" ? null : Number(form.outdoor_parking),
        monthly_fees: form.monthly_fees === "" ? null : Number(form.monthly_fees),
        // iter v28 r278 — Numerici / bool extra
        cellar_sqm: form.cellar_sqm === "" ? null : Number(form.cellar_sqm),
        attic_sqm: form.attic_sqm === "" ? null : Number(form.attic_sqm),
      };
      // Rimuovi chiavi non accettate dal backend (id, user_id, photos ecc.)
      delete payload.id;
      delete payload.user_id;
      delete payload.photos;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.autopilot;
      delete payload.social_studio_style;
      delete payload.social_photo_ranking;
      delete payload.previous_price;
      delete payload.days_to_sell;
      delete payload.listing_id;
      delete payload.public_id;

      const { data } = await api.put(`/properties/${property.id}`, payload);
      toast.success("Dati aggiornati");
      // Iter 261: chiudi PRIMA del setProperty → evita race con useEffect
      // che reimposta `form` mentre il modal è ancora visibile.
      onClose?.();
      onSaved?.(data);
    } catch (err) {
      console.error("update property failed", err);
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-black/45 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => {
        // iter 313 r2 — il modal era figlio del <Link> della card → click sul
        // backdrop navigava al dettaglio. Portal + stopPropagation lo risolvono.
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          onClose?.();
        }
      }}
      data-testid="property-edit-modal"
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-2xl bg-white sm:rounded-xl rounded-t-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7E0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
              Modifica dati immobile
            </div>
            <div className="font-editorial text-xl mt-0.5 truncate">
              {property?.title || "Immobile"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 text-[#6B6A65] hover:text-[#1C1C1A]"
            data-testid="property-edit-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {/* iter v28 r320 — Counter completezza Immobiliare.it */}
          <ImmobiliareCompletenessBanner form={form} />
          {/* iter v28 r321 — Counter completezza Idealista */}
          <IdealistaCompletenessBanner form={form} />
          <div className="space-y-4">
            <Field label="Titolo dell'annuncio" hint="Es. 'Trilocale ristrutturato · Brera, Milano'">
              <input data-vista type="text" value={form.title || ""} onChange={update("title")} className="w-full" data-testid="edit-field-title" />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipologia">
                <select data-vista value={form.property_type || ""} onChange={update("property_type")} className="w-full" data-testid="edit-field-property-type">
                  {["Appartamento","Villa","Loft","Attico","Bilocale","Trilocale","Monolocale","Ufficio","Negozio","Box","Terreno"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Vendita o affitto">
                <select data-vista value={form.listing_type || "vendita"} onChange={update("listing_type")} className="w-full" data-testid="edit-field-listing-type">
                  <option value="vendita">Vendita</option>
                  <option value="affitto">Affitto</option>
                </select>
              </Field>
            </div>

            <Field label={form.listing_type === "affitto" ? "Canone mensile (€)" : "Prezzo (€)"} hint={form.listing_type === "affitto" ? "Es. 1500" : "Es. 890000"}>
              <input data-vista type="number" min="0" value={form.price ?? ""} onChange={updateNum("price")} className="w-full disabled:bg-[#F5F4F0] disabled:text-[#9A968B]" disabled={form.hide_price} data-testid="edit-field-price" />
              <label className="mt-2 flex items-center gap-2 text-[12px] text-[#3F3D38] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.hide_price}
                  onChange={(e) => setForm((f) => ({ ...f, hide_price: e.target.checked }))}
                  className="w-3.5 h-3.5 accent-[#7C8B6E]"
                  data-testid="edit-field-hide-price"
                />
                <span>Trattativa riservata · non pubblicare il prezzo</span>
              </label>
            </Field>

            <div className="grid grid-cols-3 gap-3">
              <Field label="Mq" requiredKey="surface">
                <input data-vista type="number" min="0" value={form.surface ?? ""} onChange={updateNum("surface")} className="w-full" data-testid="edit-field-surface" />
              </Field>
              <Field label="Locali" requiredKey="rooms">
                <input data-vista type="number" min="0" value={form.rooms ?? ""} onChange={updateNum("rooms")} className="w-full" data-testid="edit-field-rooms" />
              </Field>
              <Field label="Bagni" requiredKey="bathrooms">
                <input data-vista type="number" min="0" value={form.bathrooms ?? ""} onChange={updateNum("bathrooms")} className="w-full" data-testid="edit-field-bathrooms" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Piano" requiredKey="floor" hint="Es. '3°' o 'Piano terra'">
                <input data-vista type="text" value={form.floor || ""} onChange={update("floor")} className="w-full" data-testid="edit-field-floor" />
              </Field>
              <Field label="Classe energetica" requiredKey="energy_class">
                <select data-vista value={form.energy_class || ""} onChange={update("energy_class")} className="w-full" data-testid="edit-field-energy">
                  <option value="">—</option>
                  {["A4","A3","A2","A1","A","B","C","D","E","F","G"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Città">
              <input data-vista type="text" value={form.city || ""} onChange={update("city")} className="w-full" data-testid="edit-field-city" />
            </Field>
            <Field label="Indirizzo" hint="Solo per il tuo CRM · puoi nasconderlo nei materiali pubblici">
              <input data-vista type="text" value={form.address || ""} onChange={update("address")} className="w-full" data-testid="edit-field-address" />
              <label className="mt-2 flex items-center gap-2 text-[12px] text-[#3F3D38] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.hide_address}
                  onChange={(e) => setForm((f) => ({ ...f, hide_address: e.target.checked }))}
                  className="w-3.5 h-3.5 accent-[#7C8B6E]"
                  data-testid="edit-field-hide-address"
                />
                <span>Non pubblicare via e civico · mostra solo zona/città</span>
              </label>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Anno costruzione" requiredKey="year_built">
                <input data-vista type="number" min="0" max="2100" value={form.year_built ?? ""} onChange={updateNum("year_built")} className="w-full" data-testid="edit-field-year" />
              </Field>
              <Field label="Esposizione">
                <input data-vista type="text" placeholder="Es. Sud-Ovest" value={form.exposure || ""} onChange={update("exposure")} className="w-full" data-testid="edit-field-exposure" />
              </Field>
            </div>

            <Field label="Riscaldamento" requiredKey="heating">
              <select data-vista value={form.heating || ""} onChange={update("heating")} className="w-full" data-testid="edit-field-heating">
                <option value="">—</option>
                {["Autonomo","Centralizzato","Pompa di calore","Pavimento radiante","Stufa a pellet","Assente"].map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </Field>

            {/* iter v28 r277 — Campi extra per Chrome Extension (Immobiliare ecc.) */}
            <div className="pt-4 mt-2 border-t border-[#E5E7E0]">
              <div className="text-[12px] uppercase tracking-[0.18em] font-semibold text-[#3F3D38] mb-3">
                Dati per i portali · auto-fill estensione
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Camere da letto">
                  <input data-vista type="number" min="0" value={form.bedrooms ?? ""} onChange={updateNum("bedrooms")} className="w-full" data-testid="edit-field-bedrooms" />
                </Field>
                <Field label="Cucine">
                  <input data-vista type="number" min="0" max="5" value={form.kitchens ?? ""} onChange={updateNum("kitchens")} className="w-full" data-testid="edit-field-kitchens" />
                </Field>
                <Field label="Tot. piani edificio" requiredKey="total_floors">
                  <input data-vista type="number" min="1" max="50" value={form.total_floors ?? ""} onChange={updateNum("total_floors")} className="w-full" data-testid="edit-field-total-floors" />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="IPE (kWh/m²anno)" requiredKey="ipe">
                  <input data-vista type="number" min="0" step="0.01" value={form.ipe ?? ""} onChange={updateNum("ipe")} className="w-full" data-testid="edit-field-ipe" />
                </Field>
                <Field label="Anno ristrutturazione">
                  <input data-vista type="number" min="0" max="2100" value={form.year_renovated ?? ""} onChange={updateNum("year_renovated")} className="w-full" data-testid="edit-field-year-renov" />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Climatizzatore">
                  <select data-vista value={form.air_conditioning || ""} onChange={update("air_conditioning")} className="w-full" data-testid="edit-field-ac">
                    <option value="">—</option>
                    <option value="solo_freddo">Solo freddo</option>
                    <option value="solo_caldo">Solo caldo</option>
                    <option value="caldo_freddo">Freddo/Caldo</option>
                    <option value="assente">Assente</option>
                  </select>
                </Field>
                <Field label="Arredamento">
                  <select data-vista value={form.furnished || ""} onChange={update("furnished")} className="w-full" data-testid="edit-field-furnished">
                    <option value="">—</option>
                    <option value="arredato">Arredato</option>
                    <option value="non_arredato">Non arredato</option>
                    <option value="parzialmente_arredato">Parzialmente arredato</option>
                    <option value="solo_cucina_arredata">Solo cucina arredata</option>
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Stato immobile" requiredKey="condition">
                  <select data-vista value={form.condition || ""} onChange={update("condition")} className="w-full" data-testid="edit-field-condition">
                    <option value="">—</option>
                    <option value="da_ristrutturare">Da ristrutturare</option>
                    <option value="buono">Buono stato</option>
                    <option value="ottimo">Ottimo</option>
                    <option value="nuovo">Nuovo / Ristrutturato</option>
                  </select>
                </Field>
                <Field label="Infissi">
                  <select data-vista value={form.fixtures || ""} onChange={update("fixtures")} className="w-full" data-testid="edit-field-fixtures">
                    <option value="">—</option>
                    <option value="legno">Legno</option>
                    <option value="pvc">PVC</option>
                    <option value="alluminio">Alluminio</option>
                    <option value="metallo/legno">Metallo / Legno</option>
                    <option value="altro">Altro</option>
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Tipo proprietà">
                  <select data-vista value={form.property_ownership || ""} onChange={update("property_ownership")} className="w-full" data-testid="edit-field-ownership">
                    <option value="">—</option>
                    <option value="intera_proprieta">Intera proprietà</option>
                    <option value="nuda_proprieta">Nuda proprietà</option>
                    <option value="multiproprieta">Multiproprietà</option>
                  </select>
                </Field>
                <Field label="Spese condominiali €/mese">
                  <input data-vista type="number" min="0" value={form.monthly_fees ?? ""} onChange={updateNum("monthly_fees")} className="w-full" data-testid="edit-field-monthly-fees" />
                </Field>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-3">
                <Field label="Ascensori">
                  <input data-vista type="number" min="0" max="10" value={form.elevators ?? ""} onChange={updateNum("elevators")} className="w-full" data-testid="edit-field-elevators" />
                </Field>
                <Field label="Box / Garage">
                  <input data-vista type="number" min="0" max="10" value={form.garage_spots ?? ""} onChange={updateNum("garage_spots")} className="w-full" data-testid="edit-field-garage" />
                </Field>
                <Field label="Posti auto esterni">
                  <input data-vista type="number" min="0" max="10" value={form.outdoor_parking ?? ""} onChange={updateNum("outdoor_parking")} className="w-full" data-testid="edit-field-outdoor-parking" />
                </Field>
              </div>

              {/* iter v28 r278 — Allineamento Immobiliare: campi extra */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Combustibile riscaldamento">
                  <select data-vista value={form.heating_fuel || ""} onChange={update("heating_fuel")} className="w-full" data-testid="edit-field-heating-fuel">
                    <option value="">—</option>
                    <option value="metano">Metano</option>
                    <option value="gpl">GPL</option>
                    <option value="gasolio">Gasolio</option>
                    <option value="pellet">Pellet</option>
                    <option value="elettrico">Elettrico</option>
                    <option value="biomassa">Biomassa</option>
                    <option value="teleriscaldamento">Teleriscaldamento</option>
                  </select>
                </Field>
                <Field label="Categoria catastale" requiredKey="cadastral_category">
                  <select data-vista value={form.cadastral_category || ""} onChange={update("cadastral_category")} className="w-full" data-testid="edit-field-cadastral">
                    <option value="">—</option>
                    <option value="A/1">A/1 · Signorile</option>
                    <option value="A/2">A/2 · Civile</option>
                    <option value="A/3">A/3 · Economica</option>
                    <option value="A/4">A/4 · Popolare</option>
                    <option value="A/5">A/5 · Ultrapopolare</option>
                    <option value="A/6">A/6 · Rurale</option>
                    <option value="A/7">A/7 · Villini</option>
                    <option value="A/8">A/8 · Ville</option>
                    <option value="A/9">A/9 · Castelli</option>
                    <option value="A/10">A/10 · Uffici</option>
                    <option value="A/11">A/11 · Alloggi tipici</option>
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Disponibilità">
                  <select data-vista value={form.availability_status || ""} onChange={update("availability_status")} className="w-full" data-testid="edit-field-availability">
                    <option value="">—</option>
                    <option value="libero">Libero subito</option>
                    <option value="occupato">Occupato</option>
                    <option value="libero_dal">Libero dal...</option>
                  </select>
                </Field>
                <Field label="Libero dal (se applicabile)">
                  <input data-vista type="date" value={form.available_from || ""} onChange={update("available_from")} className="w-full" data-testid="edit-field-available-from" />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Piscina">
                  <select data-vista value={form.pool || ""} onChange={update("pool")} className="w-full" data-testid="edit-field-pool">
                    <option value="">—</option>
                    <option value="privata">Privata</option>
                    <option value="condominiale">Condominiale</option>
                    <option value="assente">Assente</option>
                  </select>
                </Field>
                <Field label="Portineria">
                  <select data-vista value={form.concierge || ""} onChange={update("concierge")} className="w-full" data-testid="edit-field-concierge">
                    <option value="">—</option>
                    <option value="completa">Completa (h24)</option>
                    <option value="parziale">Parziale (part-time)</option>
                    <option value="assente">Assente</option>
                  </select>
                </Field>
              </div>

              {/* Cantina / Soffitta / Taverna / Accessibilità */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Cantina">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!form.cellar}
                      onChange={(e) => setForm((f) => ({ ...f, cellar: e.target.checked }))}
                      className="w-4 h-4 accent-[#7C8B6E]"
                      data-testid="edit-field-cellar"
                    />
                    <input data-vista type="number" min="0" step="0.5" placeholder="mq" value={form.cellar_sqm ?? ""} onChange={updateNum("cellar_sqm")} className="flex-1" data-testid="edit-field-cellar-sqm" />
                  </div>
                </Field>
                <Field label="Soffitta / Mansarda">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!form.attic}
                      onChange={(e) => setForm((f) => ({ ...f, attic: e.target.checked }))}
                      className="w-4 h-4 accent-[#7C8B6E]"
                      data-testid="edit-field-attic"
                    />
                    <input data-vista type="number" min="0" step="0.5" placeholder="mq" value={form.attic_sqm ?? ""} onChange={updateNum("attic_sqm")} className="flex-1" data-testid="edit-field-attic-sqm" />
                  </div>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Taverna">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.taverna}
                      onChange={(e) => setForm((f) => ({ ...f, taverna: e.target.checked }))}
                      className="w-4 h-4 accent-[#7C8B6E]"
                      data-testid="edit-field-taverna"
                    />
                    <span className="text-[13px] text-[#3F3D38]">Presente</span>
                  </label>
                </Field>
                <Field label="Accessibile a disabili">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.accessible}
                      onChange={(e) => setForm((f) => ({ ...f, accessible: e.target.checked }))}
                      className="w-4 h-4 accent-[#7C8B6E]"
                      data-testid="edit-field-accessible"
                    />
                    <span className="text-[13px] text-[#3F3D38]">Sì</span>
                  </label>
                </Field>
              </div>

              {/* iter v28 r345 — Caratteristiche aggiuntive (vengono pubblicate sui portali) */}
              <div className="text-[11px] uppercase tracking-wider text-[#7C8B6E] font-semibold mt-5 mb-2">
                Caratteristiche aggiuntive (pubblicate sui portali)
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Terrazzo">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.terrace}
                      onChange={(e) => setForm((f) => ({ ...f, terrace: e.target.checked }))}
                      className="w-4 h-4 accent-[#7C8B6E]"
                      data-testid="edit-field-terrace"
                    />
                    <span className="text-[13px] text-[#3F3D38]">Sì</span>
                  </label>
                </Field>
                <Field label="Balcone">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.balcony}
                      onChange={(e) => setForm((f) => ({ ...f, balcony: e.target.checked }))}
                      className="w-4 h-4 accent-[#7C8B6E]"
                      data-testid="edit-field-balcony"
                    />
                    <span className="text-[13px] text-[#3F3D38]">Sì</span>
                  </label>
                </Field>
                <Field label="Giardino">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.garden}
                      onChange={(e) => setForm((f) => ({ ...f, garden: e.target.checked }))}
                      className="w-4 h-4 accent-[#7C8B6E]"
                      data-testid="edit-field-garden"
                    />
                    <span className="text-[13px] text-[#3F3D38]">Sì</span>
                  </label>
                </Field>
                <Field label="Armadi a muro">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.wardrobe}
                      onChange={(e) => setForm((f) => ({ ...f, wardrobe: e.target.checked }))}
                      className="w-4 h-4 accent-[#7C8B6E]"
                      data-testid="edit-field-wardrobe"
                    />
                    <span className="text-[13px] text-[#3F3D38]">Sì</span>
                  </label>
                </Field>
              </div>

              {form.listing_type === "affitto" && (
                <div className="mt-3">
                  <Field label="Regime fiscale (affitto)">
                    <select data-vista value={form.contract_registration || ""} onChange={update("contract_registration")} className="w-full" data-testid="edit-field-contract-reg">
                      <option value="">—</option>
                      <option value="registrato">Registrato</option>
                      <option value="in_attesa">In attesa</option>
                      <option value="cedolare_secca">Cedolare secca</option>
                    </select>
                  </Field>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#E5E7E0] flex items-center gap-3 justify-end bg-white">
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium px-3 py-2"
            data-testid="property-edit-cancel"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-[#1C1C1A] hover:bg-black text-white py-2.5 px-5 rounded-full text-[13.5px] font-semibold disabled:opacity-60 transition-all"
            data-testid="property-edit-save"
          >
            {saving ? "Salvo…" : "Salva modifiche"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

function Field({ label, hint, requiredKey, children }) {
  // iter v28 r320 — Se requiredKey è in REQUIRED_SET, mostra asterisco rosso
  const isRequired = requiredKey && REQUIRED_SET.has(requiredKey);
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#7C8B6E] block mb-1.5">
        {label}
        {isRequired && (
          <span
            className="text-red-500 ml-1"
            title="Obbligatorio per Immobiliare.it"
            data-testid={`required-marker-${requiredKey}`}
          >
            *
          </span>
        )}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-[#9A968B] mt-1 italic">{hint}</span>}
    </label>
  );
}

// iter v28 r321 — Banner che mostra "Pronto per Idealista: X/Y campi"
// e l'elenco dei campi mancanti. Calcolato dal form corrente in tempo reale.
function IdealistaCompletenessBanner({ form }) {
  const { compiled, missing, total } = useMemo(
    () => checkIdealistaCompleteness(form),
    [form]
  );
  const allDone = compiled === total;
  return (
    <div
      className={`mb-4 px-3 py-2.5 rounded-lg border text-[12px] ${
        allDone
          ? "bg-[#F1F5EC] border-[#C8D6BB] text-[#4F5A3E]"
          : "bg-amber-50 border-amber-200 text-amber-900"
      }`}
      data-testid="idealista-completeness-banner"
    >
      <div className="flex items-center justify-between gap-2">
        <strong className="font-semibold">
          {allDone ? "✓ Pronto per Idealista" : "Pronto per Idealista"}
        </strong>
        <span className="text-[11px] font-mono">
          {compiled}/{total} campi
        </span>
      </div>
      {!allDone && missing.length > 0 && (
        <div className="mt-1.5 text-[11px] leading-snug">
          Mancano: {missing.map((m) => m.label).join(" · ")}
        </div>
      )}
    </div>
  );
}

// iter v28 r320 — Banner che mostra "Pronto per Immobiliare: X/Y campi"
// e l'elenco dei campi mancanti. Calcolato dal form corrente in tempo reale.
function ImmobiliareCompletenessBanner({ form }) {
  const { compiled, missing, total } = useMemo(
    () => checkImmobiliareCompleteness(form),
    [form]
  );
  const allDone = compiled === total;
  return (
    <div
      className={`mb-4 px-3 py-2.5 rounded-lg border text-[12px] ${
        allDone
          ? "bg-[#F1F5EC] border-[#C8D6BB] text-[#4F5A3E]"
          : "bg-amber-50 border-amber-200 text-amber-900"
      }`}
      data-testid="immobiliare-completeness-banner"
    >
      <div className="flex items-center justify-between gap-2">
        <strong className="font-semibold">
          {allDone ? "✓ Pronto per Immobiliare.it" : "Pronto per Immobiliare.it"}
        </strong>
        <span className="text-[11px] font-mono">
          {compiled}/{total} campi
        </span>
      </div>
      {!allDone && missing.length > 0 && (
        <div className="mt-1.5 text-[11px] leading-snug">
          Mancano: {missing.map((m) => m.label).join(" · ")}
        </div>
      )}
    </div>
  );
}
